<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import {
  getUnixTime,
  startOfDay,
  endOfDay,
  subDays,
  fromUnixTime,
} from 'date-fns';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';

import KpiCard from './components/KpiCard.vue';
import AgentStatusCard from './components/AgentStatusCard.vue';
import QueueCard from './components/QueueCard.vue';
import HourlyTrafficCard from './components/HourlyTrafficCard.vue';
import TimeMetricCard from './components/TimeMetricCard.vue';

const { t } = useI18n();
const store = useStore();

// Last 7 days window for the period-based metrics.
const from = getUnixTime(startOfDay(subDays(new Date(), 6)));
const to = getUnixTime(endOfDay(new Date()));

const summary = computed(() => store.getters.getAccountSummary);
const previous = computed(() => summary.value.previous || {});
const reportData = computed(() => store.getters.getAccountReports.data);
const conversationMetric = computed(
  () => store.getters.getAccountConversationMetric || {}
);
const agentStatus = computed(() => store.getters['agents/getAgentStatus']);
const heatmap = computed(
  () => store.getters.getAccountConversationHeatmapData || []
);

const seriesFor = metric =>
  (reportData.value?.[metric] || []).map(point => point.value || 0);

const conversationsSeries = computed(() => seriesFor('conversations_count'));
const resolutionsSeries = computed(() => seriesFor('resolutions_count'));
const incomingSeries = computed(() => seriesFor('incoming_messages_count'));

// Aggregate the hourly heatmap (one point per hour over 7 days) into 24 buckets
// keyed by hour-of-day, so the traffic chart shows the busiest hours.
const hourlyTraffic = computed(() => {
  const buckets = Array.from({ length: 24 }, () => 0);
  heatmap.value.forEach(point => {
    const hour = fromUnixTime(point.timestamp).getHours();
    buckets[hour] += point.value || 0;
  });
  return buckets;
});

const fetchLiveData = () => {
  store.dispatch('fetchAccountConversationMetric');
  store.dispatch('agents/get');
};

const fetchPeriodData = () => {
  const reportObj = { from, to, groupBy: 'day', businessHours: false };
  store.dispatch('fetchAccountSummary', reportObj);
  [
    'conversations_count',
    'resolutions_count',
    'incoming_messages_count',
  ].forEach(metric =>
    store.dispatch('fetchAccountReport', { metric, ...reportObj })
  );
  store.dispatch('fetchAccountConversationHeatmap', {
    metric: 'conversations_count',
    from,
    to,
    businessHours: false,
  });
};

const { startRefetching } = useLiveRefresh(fetchLiveData, 60000);

onMounted(() => {
  fetchLiveData();
  fetchPeriodData();
  startRefetching();
});
</script>

<template>
  <main class="flex-1 overflow-y-auto bg-n-solid-1 dark:bg-n-background p-6">
    <div class="max-w-[1600px] mx-auto space-y-6">
      <!-- KPI row -->
      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <KpiCard
          icon="i-lucide-messages-square"
          :title="t('PAINEL_PRINCIPAL.KPI.CONVERSATIONS')"
          :value="summary.conversations_count"
          :series="conversationsSeries"
          :subtitle="t('PAINEL_PRINCIPAL.KPI.LAST_7_DAYS')"
        />
        <KpiCard
          icon="i-lucide-circle-check-big"
          :title="t('PAINEL_PRINCIPAL.KPI.RESOLUTIONS')"
          :value="summary.resolutions_count"
          :series="resolutionsSeries"
          :subtitle="t('PAINEL_PRINCIPAL.KPI.LAST_7_DAYS')"
        />
        <KpiCard
          icon="i-lucide-inbox"
          :title="t('PAINEL_PRINCIPAL.KPI.INCOMING_MESSAGES')"
          :value="summary.incoming_messages_count"
          :series="incomingSeries"
          :subtitle="t('PAINEL_PRINCIPAL.KPI.LAST_7_DAYS')"
        />
        <KpiCard
          icon="i-lucide-message-circle"
          :title="t('PAINEL_PRINCIPAL.KPI.OPEN_NOW')"
          :value="conversationMetric.open || 0"
          :series="conversationsSeries"
          live
        />
      </div>

      <!-- Operational row -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <AgentStatusCard
          :online="agentStatus.online"
          :busy="agentStatus.busy"
          :offline="agentStatus.offline"
        />
        <QueueCard
          :open="conversationMetric.open || 0"
          :unattended="conversationMetric.unattended || 0"
          :unassigned="conversationMetric.unassigned || 0"
          :pending="conversationMetric.pending || 0"
        />
        <HourlyTrafficCard :hours="hourlyTraffic" />
      </div>

      <!-- SLA row -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <TimeMetricCard
          icon="i-lucide-timer"
          :title="t('PAINEL_PRINCIPAL.TIME.FIRST_RESPONSE')"
          :value="summary.avg_first_response_time"
          :previous="previous.avg_first_response_time"
        />
        <TimeMetricCard
          icon="i-lucide-flag"
          :title="t('PAINEL_PRINCIPAL.TIME.RESOLUTION')"
          :value="summary.avg_resolution_time"
          :previous="previous.avg_resolution_time"
        />
      </div>
    </div>
  </main>
</template>
